export const emptyAnswers = {
  purpose: "",
  demoMode: "",
  doctor: null,
  patientType: "",
  encounter: "",
  ehr: "",
  specialty: "",
  date: "",
  syntheticNote: "",
  noteApproved: false,
  soapNoteTemplate: "",
  conversationPrompt: "",
  // The patient case the note was written about; the server builds the facesheet from it.
  facesheetCase: null,
};

export const options = {
  purpose: [["onboarding", "Onboarding call"], ["sales", "Sales demo"]],
  demoMode: [["integrated", "Integrated"], ["standalone", "Standalone"]],
  patientType: [["new", "New"], ["fu", "Followup"], ["telemed", "Telemed"]],
  encounter: [
    ["review", "Review — with a pre-populated note"],
    ["empty", "Empty encounter — ready to record"],
  ],
  ehr: [["OncoEMR", "OncoEMR"], ["4D EMR", "4D EMR"], ["ARIA", "ARIA"], ["Meditab", "Meditab"]],
};

// Integrated demos can only be created for these EHRs; the rest are listed but unavailable.
export const availableIntegratedEhrs = ["OncoEMR"];
export const integratedAccount = { email: "jocelyn@knowtex.ai", organization: "Knowtex", superOrg: "Knowtex", specialty: "Astera" };

export function getMinimumDate(answers = {}) {
  // Keep aligned with LATEST_RESULT_DATE in server/utils/oncoemrDemoFacesheet.js.
  return answers.purpose === "sales" && answers.demoMode === "integrated" ? "2026-05-27" : "1900-01-01";
}

export function isUnavailableIntegratedEhr(answers) {
  return answers.purpose === "sales" && answers.demoMode === "integrated" && Boolean(answers.ehr) && !availableIntegratedEhrs.includes(answers.ehr);
}

export const questions = {
  purpose: "Is this an onboarding call or a sales demo?",
  demoMode: "Are you demoing integrated or standalone?",
  doctor: "What user is this test patient for?",
  patientType: "What patient type do you want?",
  encounter: "Do you want a patient set to Review with a pre-populated note, or an empty patient encounter you can record in?",
  ehr: "What EHR are you demoing?",
  specialty: "What specialty are you demoing?",
  date: "What date do you want this patient to appear in?",
};

export function getSteps(answers) {
  if (answers.purpose === "onboarding") {
    return ["purpose", "doctor", "patientType", "encounter", "date"];
  }
  if (answers.purpose === "sales") {
    if (answers.demoMode === "integrated") {
      // An unavailable EHR ends the flow so no patient can be requested for it.
      if (isUnavailableIntegratedEhr(answers)) return ["purpose", "demoMode", "ehr"];
      return ["purpose", "demoMode", "ehr", "date"];
    }
    if (answers.demoMode === "standalone") return ["purpose", "demoMode", "doctor", "specialty", "date"];
    return ["purpose", "demoMode"];
  }
  return ["purpose"];
}

export function isAnswerComplete(field, value, answers = {}) {
  if (field === "doctor") return Boolean(value?.user_id);
  if (field === "encounter" && value === "review") {
    return Boolean(answers.noteApproved && answers.syntheticNote?.trim() && answers.facesheetCase);
  }
  if (field === "date") {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value < getMinimumDate(answers)) return false;
    const parsed = new Date(`${value}T12:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }
  return Boolean(value);
}

// Changing an earlier answer invalidates every later answer, including hidden branches.
export function updateAnswer(answers, field, value) {
  const steps = getSteps(answers);
  const index = steps.indexOf(field);
  if (index < 0) return answers;
  const next = { ...emptyAnswers };
  steps.slice(0, index).forEach((key) => { next[key] = answers[key]; });
  const encounterIndex = steps.indexOf("encounter");
  if (encounterIndex >= 0 && index > encounterIndex && answers.encounter === "review") {
    next.syntheticNote = answers.syntheticNote;
    next.noteApproved = answers.noteApproved;
    next.soapNoteTemplate = answers.soapNoteTemplate;
    next.conversationPrompt = answers.conversationPrompt;
    next.facesheetCase = answers.facesheetCase;
  }
  next[field] = value;
  return next;
}

export function buildRequest(answers) {
  const request = { purpose: answers.purpose, date: answers.date };
  if (answers.purpose === "onboarding") {
    return {
      ...request,
      userId: answers.doctor.user_id,
      patientType: answers.patientType,
      encounter: answers.encounter,
      ...(answers.encounter === "review" ? {
        syntheticNote: answers.syntheticNote,
        noteApproved: answers.noteApproved,
        soapNoteTemplate: answers.soapNoteTemplate,
        conversationPrompt: answers.conversationPrompt,
        facesheetCase: answers.facesheetCase,
      } : {}),
    };
  }
  if (answers.demoMode === "integrated") {
    return { ...request, demoMode: "integrated", ehr: answers.ehr };
  }
  const salesRequest = { ...request, userId: answers.doctor.user_id, specialty: answers.specialty };
  return { ...salesRequest, demoMode: "standalone" };
}
